/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Presentational layout for the appointment registration modal.
 * @module modals/AppointmentRegistration/AppointmentRegistrationView
 */

import { createPortal } from "react-dom";
import Select from "react-select";
import { CircularProgress } from "@mui/material";

import AppointmentInformation from "../../components/AppointmentRegistrationSections/AppointmentInformation";
import CorporateIdentity from "../../components/AppointmentRegistrationSections/CorporateIdentity";
import ContactInformation from "../../components/AppointmentRegistrationSections/ContactInformation";
import Location from "../../components/AppointmentRegistrationSections/Location";
import CommercialOffer from "../../components/AppointmentRegistrationSections/CommercialOffer";
import Documents from "../../components/AppointmentRegistrationSections/Documents";
import Certifications from "../../components/AppointmentRegistrationSections/Certifications";
import ConfirmationModal from "../ConfirmationModal/index.jsx";
import {
  buildRegistrationSectionKey,
  CERTIFICATIONS_FIELDS,
  COMMERCIAL_OFFER_FIELDS,
  companyHasAnyField,
  CONTACT_INFORMATION_FIELDS,
  CORPORATE_IDENTITY_FIELDS,
  DOCUMENTS_FIELDS,
  LOCATION_FIELDS,
} from "../../utils/appointmentRegistrationUtils.js";
import styles from "./index.module.css";

/**
 * Renders the appointment registration dialog shell and section blocks.
 * @param {Object} props - View-model from `useAppointmentRegistrationForm`
 * @returns {JSX.Element}
 */
const AppointmentRegistrationView = ({
  t,
  dialogRef,
  selectedRequirement,
  company,
  displayCompany,
  selectedProfile,
  category,
  missingRequiredFields,
  locationReferenceData,
  isFormLocked,
  isSubmitting,
  loading,
  showConfirmationModal,
  confirmationDialogRef,
  profileOptions,
  profileSelectStyles,
  selectedProfileOption,
  getFieldValidation,
  handleRequestClose,
  handleProfileSelectChange,
  handleCorporateIdentityFieldChange,
  handleContactInformationFieldChange,
  handleLocationFieldChange,
  handleCommercialOfferFieldChange,
  handleDocumentsFieldChange,
  handleCertificationsFieldChange,
  handleCorporateIdentityValidationChange,
  handleContactInformationValidationChange,
  handleLocationValidationChange,
  handleCommercialOfferValidationChange,
  handleDocumentsValidationChange,
  handleCertificationsValidationChange,
  checkAppointmentsAndProceed,
  handleConfirmationModalConfirm,
  handleConfirmationModalCancel,
  isButtonDisabled,
}) => (
  <>
    <dialog
      ref={dialogRef}
      className={styles.requirementsModal}
      aria-busy={isFormLocked}
    >
      {isFormLocked && (
        <div className={styles.submittingOverlay} aria-hidden>
          {isSubmitting && <CircularProgress size={40} />}
        </div>
      )}
      <div className={styles.closeButtonContainer}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleRequestClose}
          disabled={isFormLocked}
        >
          &times;
        </button>
      </div>
      <div className={styles.modalHeader}>
        <span className={styles.title}>
          {t("components.appointmentRegistrationModal.title")}
        </span>
        <span className={styles.modalSubtitle}>
          {t("components.appointmentRegistrationModal.description")}
        </span>
        <div className={styles.instructionsContainer}>
          <span className={styles.modalInstructions}>
            {t("components.appointmentRegistrationModal.instructions")}
          </span>
        </div>
      </div>
      <div className={styles.modalContent} inert={isFormLocked || undefined}>
        <AppointmentInformation
          requirement={selectedRequirement}
          category={category}
        />

        {company && (
          <div className={styles.profileSelectContainer}>
            <label className={styles.profileSelectLabel}>
              {t("pages.companyProfile.availableProfiles")}
            </label>
            <Select
              value={selectedProfileOption}
              options={profileOptions}
              onChange={handleProfileSelectChange}
              isDisabled={isFormLocked}
              styles={profileSelectStyles}
              className="select__control"
            />
          </div>
        )}

        {displayCompany &&
          companyHasAnyField(displayCompany, CORPORATE_IDENTITY_FIELDS) && (
            <CorporateIdentity
              company={displayCompany}
              onFieldChange={handleCorporateIdentityFieldChange}
              onValidationChange={handleCorporateIdentityValidationChange}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              key={buildRegistrationSectionKey(
                "corporate-identity",
                company,
                selectedProfile,
              )}
            />
          )}

        {displayCompany &&
          companyHasAnyField(displayCompany, CONTACT_INFORMATION_FIELDS) && (
            <ContactInformation
              company={displayCompany}
              onFieldChange={handleContactInformationFieldChange}
              onValidationChange={handleContactInformationValidationChange}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              key={buildRegistrationSectionKey(
                "contact-info",
                company,
                selectedProfile,
              )}
            />
          )}

        {displayCompany &&
          companyHasAnyField(displayCompany, LOCATION_FIELDS) && (
            <Location
              company={displayCompany}
              onFieldChange={handleLocationFieldChange}
              onValidationChange={handleLocationValidationChange}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              referenceData={locationReferenceData}
              key={buildRegistrationSectionKey(
                "location",
                company,
                selectedProfile,
              )}
            />
          )}

        {displayCompany &&
          companyHasAnyField(displayCompany, COMMERCIAL_OFFER_FIELDS) && (
            <CommercialOffer
              company={displayCompany}
              onFieldChange={handleCommercialOfferFieldChange}
              onValidationChange={handleCommercialOfferValidationChange}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              key={buildRegistrationSectionKey(
                "commercial-offer",
                company,
                selectedProfile,
              )}
            />
          )}

        {displayCompany &&
          companyHasAnyField(displayCompany, DOCUMENTS_FIELDS) && (
            <Documents
              company={displayCompany}
              onFieldChange={handleDocumentsFieldChange}
              onValidationChange={handleDocumentsValidationChange}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              key={buildRegistrationSectionKey(
                "documents",
                company,
                selectedProfile,
              )}
            />
          )}

        {displayCompany &&
          companyHasAnyField(displayCompany, CERTIFICATIONS_FIELDS) && (
            <Certifications
              company={displayCompany}
              onFieldChange={handleCertificationsFieldChange}
              onValidationChange={handleCertificationsValidationChange}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              key={buildRegistrationSectionKey(
                "certifications",
                company,
                selectedProfile,
              )}
            />
          )}
      </div>
      <div className={styles.modalButtons}>
        <button
          type="button"
          onClick={handleRequestClose}
          className={styles.cancelButton}
          disabled={isFormLocked}
        >
          {t("components.appointmentRegistrationModal.cancelButton")}
        </button>
        <button
          type="button"
          className={styles.confirmButton}
          disabled={isButtonDisabled}
          onClick={checkAppointmentsAndProceed}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            t("components.appointmentRegistrationModal.submitButton")
          )}
        </button>
      </div>
    </dialog>

    {showConfirmationModal &&
      createPortal(
        <ConfirmationModal
          dialogRef={confirmationDialogRef}
          open={showConfirmationModal}
          onClose={handleConfirmationModalCancel}
          onConfirm={handleConfirmationModalConfirm}
          title={t("components.appointmentRegistrationModal.maxRequestsTitle")}
          message={t("components.appointmentRegistrationModal.maxRequestsMessage")}
          confirmButtonText={t("common.continue")}
          cancelButtonText={t("common.cancel")}
          isLoading={loading}
          loadingButtonText={t("common.saving")}
        />,
        document.body,
      )}
  </>
);

export default AppointmentRegistrationView;
